/**
 * Every recurring delivery, worst first, each saying whether it will do what
 * its row claims.
 *
 * The tab it replaces listed seven schedules, all "draft", with a Next run of
 * "43d ago". A schedule that is not running has no next run; this tab says it
 * is a draft or paused, why, and what would change that.
 */
import { useCallback, useMemo } from 'react'
import { Bell, BellRing, Pause, Play } from 'lucide-react'
import clsx from 'clsx'
import type { ReportSchedule } from '@/pages/pipeline-intelligence/types'
import { SchedulerStatusNotice } from '../SchedulerStatusNotice'
import { displayTemplate } from './catalog'
import { formatWhen, plural } from './format'
import type { LibraryReport } from './library'
import { HEALTH_SEVERITY, describeCadence, scheduleHealth, type ScheduleHealth } from './scheduleHealth'
import { Btn, Dot, EmptyState, ErrorBanner, FOCUS, Panel, Pill, SkeletonRows } from './ui'
import type { ReportActions } from './useReportActions'
import type { ReportingData, ReportPermissions } from './useReportingData'

type Row = {
  schedule: ReportSchedule
  health: ScheduleHealth
  /** The library report it belongs to, when there is one. */
  report: LibraryReport | null
}

const NO_EDIT = 'Your role can view and run reports but not change them.'
const NO_APPROVE = 'Activating a schedule needs the approve permission.'
const NO_RUN = 'Your role cannot run reports.'

export function SchedulesTab({
  data,
  actions,
  perms,
  query,
  onOpenReport,
  onEditSchedule,
}: {
  data: ReportingData
  actions: ReportActions
  perms: ReportPermissions
  query: string
  onOpenReport: (report: LibraryReport) => void
  onEditSchedule: (schedule: ReportSchedule) => void
}) {
  const eventLabel = useCallback(
    (type: string) => data.status?.productEvents?.find((event) => event.type === type)?.label ?? type,
    [data.status],
  )
  const rows = useMemo<Row[]>(() => {
    const reportOf = new Map<string, LibraryReport>()
    for (const report of data.library) for (const schedule of report.schedules) reportOf.set(schedule.id, report)
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
    return data.schedules
      .map((schedule) => ({
        schedule,
        health: scheduleHealth(schedule, { now: data.now, status: data.status, eventLabel }),
        report: reportOf.get(schedule.id) ?? null,
      }))
      .filter((row) => {
        if (!tokens.length) return true
        const text = [row.schedule.name, displayTemplate(row.schedule.template), ...row.schedule.recipients, row.schedule.createdBy ?? '']
          .join(' ')
          .toLowerCase()
        return tokens.every((token) => text.includes(token))
      })
      .sort(
        (a, b) =>
          HEALTH_SEVERITY[a.health.state] - HEALTH_SEVERITY[b.health.state] ||
          a.schedule.name.localeCompare(b.schedule.name, 'en-US', { sensitivity: 'base' }),
      )
  }, [data.library, data.schedules, data.now, data.status, query, eventLabel])

  const me = (data.me ?? '').toLowerCase()

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-10 pt-5 sm:px-6">
      <p className="max-w-3xl text-[13px] text-[#9A9A9A]">
        Every recurring delivery the desk runs. Subscribing puts{' '}
        <span className="text-[#FFE9A8]">{data.me ?? 'your address'}</span> on the list; it does not change the report,
        the cadence, or anyone else’s copy.
      </p>
      <SchedulerStatusNotice />
      {data.failures
        .filter((failure) => failure.key === 'schedules')
        .map((failure) => (
          <ErrorBanner key={failure.key} onRetry={failure.retry}>
            Could not read schedules, so none are listed.
          </ErrorBanner>
        ))}

      {data.loading ? (
        <SkeletonRows label="Loading schedules" count={3} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={data.schedules.length ? 'No schedule matches' : 'No schedules yet'}
          body={
            data.schedules.length
              ? 'Clear the search to see every schedule.'
              : 'Schedule a report from the library and it is listed here, with whether it will really run and be emailed.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2.5" aria-label="Schedules">
          {rows.map(({ schedule, health, report }) => {
            const subscribed = Boolean(me) && schedule.recipients.some((to) => to.toLowerCase() === me)
            const toggleAllowed = schedule.isActive ? perms.canEdit : perms.canApprove
            const busy = actions.toggleSchedule.isPending || actions.runSchedule.isPending || actions.subscribe.isPending
            return (
              <li key={schedule.id}>
                <Panel as="article" tone={health.tone === 'bad' ? 'bad' : 'plain'} className="flex flex-col gap-3 p-4">
                  <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                    <div className="min-w-0 flex-[2_1_260px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={!report}
                          onClick={() => report && onOpenReport(report)}
                          className={clsx(
                            'rounded text-left text-[14px] font-semibold text-[#F5F3EE] enabled:hover:text-white disabled:cursor-default',
                            FOCUS,
                          )}
                        >
                          {schedule.name}
                        </button>
                        <Pill tone={health.tone}>
                          <Dot tone={health.tone} />
                          <span className="ml-1.5">{health.label}</span>
                        </Pill>
                      </div>
                      <p className="mt-1 text-[12.5px] text-[#9A9A9A]">
                        {displayTemplate(schedule.template)} · {describeCadence(schedule, eventLabel)}
                      </p>
                      <p className="mt-1 text-[12.5px] text-[#B9B9B9]">{health.detail}</p>
                    </div>
                    <dl className="flex min-w-[150px] flex-[1_1_150px] flex-col gap-0.5 text-[13px]">
                      <dt className="text-[11px] uppercase tracking-[.12em] text-[#8C8C8C]">Next run</dt>
                      <dd className={health.nextRunAt ? 'text-[#ECEAE4]' : 'text-[#8F8F8F]'}>{health.nextRunText}</dd>
                    </dl>
                    <dl className="flex min-w-[150px] flex-[1_1_150px] flex-col gap-0.5 text-[13px]">
                      <dt className="text-[11px] uppercase tracking-[.12em] text-[#8C8C8C]">Last run</dt>
                      <dd className="text-[#ECEAE4]">
                        {schedule.lastRunAt
                          ? formatWhen(schedule.lastRunAt, { now: data.now, timeZone: schedule.timezone, withZone: true })
                          : 'Never'}
                      </dd>
                    </dl>
                    <dl className="flex min-w-[170px] flex-[1_1_170px] flex-col gap-0.5 text-[13px]">
                      <dt className="text-[11px] uppercase tracking-[.12em] text-[#8C8C8C]">Email</dt>
                      <dd className="text-[#ECEAE4]">
                        {schedule.deliveryMode === 'none'
                          ? 'Retain only, no email'
                          : `${plural(schedule.recipients.length, 'recipient')}`}
                      </dd>
                      <dd className="line-clamp-3 text-[12px] text-[#8F8F8F]" title={schedule.deliveryBlockers?.[0]?.label}>
                        {schedule.deliveryMode === 'none'
                          ? 'Files are kept, nothing is sent.'
                          : schedule.willEmail === true
                            ? 'Will be emailed when it runs.'
                            : schedule.willEmail === false && schedule.deliveryBlockers?.length
                              ? `Not emailed: ${schedule.deliveryBlockers[0].label}`
                              : ''}
                      </dd>
                    </dl>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Btn
                      size="sm"
                      icon={schedule.isActive ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
                      disabled={!toggleAllowed || busy}
                      title={toggleAllowed ? undefined : schedule.isActive ? NO_EDIT : NO_APPROVE}
                      onClick={() => actions.toggleSchedule.mutate(schedule)}
                      aria-label={`${schedule.isActive ? 'Pause' : 'Activate'} ${schedule.name}`}
                    >
                      {schedule.isActive ? 'Pause' : 'Activate'}
                    </Btn>
                    <Btn
                      size="sm"
                      disabled={!perms.canRun || busy}
                      title={perms.canRun ? 'Make a preview now. Nothing is emailed.' : NO_RUN}
                      onClick={() => actions.runSchedule.mutate(schedule)}
                      aria-label={`Run ${schedule.name} now`}
                    >
                      Run now
                    </Btn>
                    <Btn
                      size="sm"
                      disabled={!perms.canEdit}
                      title={perms.canEdit ? 'Changing a schedule pauses it until it is activated again.' : NO_EDIT}
                      onClick={() => onEditSchedule(schedule)}
                      aria-label={`Edit schedule ${schedule.name}`}
                    >
                      Edit
                    </Btn>
                    {data.me ? (
                      <Btn
                        size="sm"
                        icon={subscribed ? <BellRing className="h-3.5 w-3.5" aria-hidden /> : <Bell className="h-3.5 w-3.5" aria-hidden />}
                        disabled={!perms.canEdit || busy}
                        title={perms.canEdit ? 'Adds or removes your address. This pauses the schedule until it is activated again.' : NO_EDIT}
                        onClick={() => actions.subscribe.mutate(schedule)}
                        aria-label={`${subscribed ? 'Unsubscribe from' : 'Subscribe to'} ${schedule.name}`}
                      >
                        {subscribed ? 'Unsubscribe me' : 'Subscribe me'}
                      </Btn>
                    ) : null}
                    {report ? (
                      <Btn size="sm" variant="quiet" onClick={() => onOpenReport(report)} aria-label={`Open the full page for ${schedule.name}`}>
                        Open report
                      </Btn>
                    ) : null}
                  </div>
                </Panel>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
